import type { AgentInfo } from '@clawlet/gateway-client';

interface AgentIconBarProps {
  agents: AgentInfo[];
  inputValue: string;
  onAgentClick: (agentName: string) => void;
}

const AGENT_EMOJIS: Record<string, string> = {
  main: '🔥',
  flint: '🔥',
  yuki: '🌸',
  researcher: '🔬',
  coder: '☕',
};

export default function AgentIconBar({ agents, inputValue, onAgentClick }: AgentIconBarProps) {
  const visibleAgents = agents.filter((a) => a.identity != null);

  if (visibleAgents.length === 0) return null;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        padding: '0.5rem 1rem',
        borderBottom: '1px solid var(--theme-border)',
        background: 'var(--theme-card)',
      }}
    >
      <span
        style={{
          fontFamily: 'var(--app-code-font)',
          fontSize: '0.55rem',
          fontWeight: 700,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: 'var(--theme-muted-foreground)',
          flexShrink: 0,
          marginRight: '0.25rem',
        }}
      >
        ASK
      </span>
      {visibleAgents.map((agent) => {
        const name = agent.identity?.name ?? agent.name ?? agent.id;
        const emoji = agent.identity?.emoji ?? AGENT_EMOJIS[agent.id.toLowerCase()] ?? '🤖';
        const atMention = `@${name}`;
        const isActive = inputValue.toLowerCase().includes(atMention.toLowerCase());

        return (
          <button
            key={agent.id}
            type="button"
            onClick={() => onAgentClick(name)}
            title={`Ask ${name}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.35rem',
              padding: '0.2rem 0.5rem',
              borderRadius: '4px',
              border: isActive
                ? '1px solid color-mix(in srgb, var(--theme-primary) 40%, transparent)'
                : '1px solid color-mix(in srgb, var(--theme-primary) 15%, transparent)',
              background: isActive
                ? 'var(--theme-accent)'
                : 'transparent',
              cursor: 'pointer',
              transition: 'all 0.15s ease',
              outline: 'none',
            }}
            onMouseEnter={(e) => {
              if (!isActive) {
                (e.currentTarget as HTMLButtonElement).style.background = 'var(--theme-accent)';
                (e.currentTarget as HTMLButtonElement).style.borderColor = 'color-mix(in srgb, var(--theme-primary) 30%, transparent)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isActive) {
                (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                (e.currentTarget as HTMLButtonElement).style.borderColor = 'color-mix(in srgb, var(--theme-primary) 15%, transparent)';
              }
            }}
          >
            {/* Agent avatar */}
            <div
              style={{
                width: '20px',
                height: '20px',
                borderRadius: '4px',
                background: 'var(--theme-accent)',
                border: '1px solid var(--theme-primary-20)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.75rem',
                flexShrink: 0,
              }}
            >
              {emoji}
            </div>
            <span
              style={{
                fontFamily: 'var(--app-code-font)',
                fontSize: '0.6rem',
                fontWeight: 700,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                color: isActive ? 'var(--theme-primary)' : 'var(--theme-muted-foreground)',
              }}
            >
              {name}
            </span>
          </button>
        );
      })}
    </div>
  );
}
